import React, { useState } from "react";
import { Box, Popover, Slider, TextField, InputAdornment } from "@mui/material";
import { toast } from "react-toastify";
import { updateWeeklyTaskAllocation } from "../../apis/apiService";
import { useTheme } from "../../ThemeContext";

const CompletionCell = ({ value: initialValue, row, setRows, login_user }) => {
  const { isDarkMode } = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(initialValue ?? 0);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async () => {
    setAnchorEl(null);

    if (value !== row.completed_percentage) {
      const oldValue = row.completed_percentage;

      // 🔹 Optimistic UI update
      setRows((prevRows) =>
        prevRows.map((r) =>
          r.project_id === row.project_id
            ? { ...r, completed_percentage: value }
            : r
        )
      );

      try {
        const payload = {
          project_id: row.project_id,
          current_status_id: row.status_name?.status_id,
          completed_percentage: value,
        };

        await updateWeeklyTaskAllocation(
          "task-allocation/updateWeeklyTaskAllocations",
          payload,
          login_user?.token
        );

        toast.success("Completion updated successfully!");
      } catch (error) {
        toast.error("Failed to update completion");

        // 🔹 Rollback
        setRows((prevRows) =>
          prevRows.map((r) =>
            r.project_id === row.project_id
              ? { ...r, completed_percentage: oldValue }
              : r
          )
        );
      }
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* ✅ Percentage Box */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          onClick={handleClick}
          sx={{
            backgroundColor: isDarkMode ? "rgba(0, 204, 0, 0.15)" : "#e6ffe6",
            border: isDarkMode ? "1.5px solid #00ff66" : "2px solid #00cc00",
            color: isDarkMode ? "#00ff66" : "#00cc00",
            // boxShadow: isDarkMode ? "0 0 8px rgba(0, 255, 102, 0.25)" : "none",
            borderRadius: "6px",
            padding: "2px 8px",
            fontSize: "14px",
            fontWeight: isDarkMode ? "600" : "500",
            minWidth: "50px",
            minHeight: "28px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              // boxShadow: isDarkMode ? "0 0 12px rgba(0, 255, 102, 0.4)" : "none",
              transform: "scale(1.05)",
            }
          }}
        >
          {value} %
        </Box>
      </Box>

      {/* ✅ Popover with Slider + Input */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          {/* Slider */}
          <Slider
            value={typeof value === "number" ? value : 0}
            onChange={(e, newValue) => setValue(newValue)}
            sx={{
              width: 120,
              color: "green",
            }}
          />

          {/* Input Field */}
          <TextField
            type="number"
            value={value === "" ? "" : Number(value)}
            onChange={(e) => {
              let input = e.target.value;

              if (input === "") {
                setValue(""); // allow temporary empty
                return;
              }

              let num = Number(input);

              if (num < 0) num = 0;
              if (num > 100) num = 100;

              setValue(num); // store normalized number
            }}
            onBlur={() => {
              if (value === "") setValue(0); // reset empty to 0
            }}
            size="small"
            sx={{ width: 70 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default CompletionCell;
